import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Filter, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Command } from "@/components/ui/command";
import { CommandInput } from "@/components/ui/command";
import { CommandList } from "@/components/ui/command";
import { CommandGroup } from "@/components/ui/command";
import { CommandItem } from "@/components/ui/command";
import { CommandEmpty } from "@/components/ui/command";
import { Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

export default function ProductFilterDropdown({
    table,
    columnFilters,
    setPriceRange,
    priceRange,
    maxPrice,
    overallMaxPrice,
    shop,
}: {
    table: any;
    columnFilters: any;
    setPriceRange: (value: [number, number]) => void;
    priceRange: [number, number];
    maxPrice: number;
    overallMaxPrice: number;
    shop: any;
}) {
    const searchMetadata = shop?.metadata?.search_metadata;

    // Add this new function to get unique categories
    const getUniqueCategories = () => {
        return searchMetadata?.categories || [];
    };

    // Inside the ProductTable component, add this helper function near getUniqueCategories
    const getUniqueProductTypes = () => {
        return searchMetadata?.types || [];
    };

    // Add this helper function near the other filter helpers
    const getUniqueTags = () => {
        return searchMetadata?.tags || [];
    };

    return (
        <div className="flex gap-2 ml-auto">
            <Popover>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="flex gap-2">
                        <Filter className="h-4 w-4" />
                        Filter
                        {
                            <Badge
                                variant="secondary"
                                className="rounded-sm px-1 font-normal"
                            >
                                {Object.keys(columnFilters).length}
                            </Badge>
                        }
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[600px]">
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <h4 className="font-medium leading-none">
                                Filter products
                            </h4>
                            <p className="text-sm text-muted-foreground">
                                Filter products by name and category
                            </p>
                        </div>

                        <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="product">Search</Label>
                            <Input
                                id="product"
                                className="col-span-2 h-8"
                                value={
                                    (table
                                        .getColumn("product_name")
                                        ?.getFilterValue() as string) ?? ""
                                }
                                onChange={(event) => {
                                    table
                                        .getColumn("product_name")
                                        ?.setFilterValue(event.target.value);
                                }}
                                placeholder="Search products, variants, or SKUs..."
                            />
                        </div>

                        <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="category">Category</Label>
                            <div className="col-span-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between"
                                        >
                                            {(table
                                                .getColumn("category")
                                                ?.getFilterValue() as string) ||
                                                "All Categories"}
                                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[200px] p-0">
                                        <Command>
                                            <CommandInput placeholder="Search categories..." />
                                            <CommandList>
                                                <CommandEmpty>
                                                    No categories found.
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    <CommandItem
                                                        onSelect={() => {
                                                            table
                                                                .getColumn(
                                                                    "category"
                                                                )
                                                                ?.setFilterValue(
                                                                    null
                                                                );
                                                        }}
                                                        className="cursor-pointer"
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                !table
                                                                    .getColumn(
                                                                        "category"
                                                                    )
                                                                    ?.getFilterValue()
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            )}
                                                        />
                                                        All Categories
                                                    </CommandItem>
                                                    {getUniqueCategories().map(
                                                        (category: any) => (
                                                            <CommandItem
                                                                key={category}
                                                                onSelect={() => {
                                                                    table
                                                                        .getColumn(
                                                                            "category"
                                                                        )
                                                                        ?.setFilterValue(
                                                                            category
                                                                        );
                                                                }}
                                                                className="cursor-pointer"
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        table
                                                                            .getColumn(
                                                                                "category"
                                                                            )
                                                                            ?.getFilterValue() ===
                                                                            category
                                                                            ? "opacity-100"
                                                                            : "opacity-0"
                                                                    )}
                                                                />
                                                                {category}
                                                            </CommandItem>
                                                        )
                                                    )}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="product-type">Product Type</Label>
                            <div className="col-span-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between"
                                        >
                                            {(table
                                                .getColumn("product_type")
                                                ?.getFilterValue() as string) ||
                                                "All Types"}
                                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[200px] p-0">
                                        <Command>
                                            <CommandInput placeholder="Search product types..." />
                                            <CommandList>
                                                <CommandEmpty>
                                                    No product types found.
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    <CommandItem
                                                        onSelect={() => {
                                                            table
                                                                .getColumn(
                                                                    "product_type"
                                                                )
                                                                ?.setFilterValue(
                                                                    null
                                                                );
                                                        }}
                                                        className="cursor-pointer"
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                !table
                                                                    .getColumn(
                                                                        "product_type"
                                                                    )
                                                                    ?.getFilterValue()
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            )}
                                                        />
                                                        All Types
                                                    </CommandItem>
                                                    {getUniqueProductTypes().map(
                                                        (type: any) => (
                                                            <CommandItem
                                                                key={type}
                                                                onSelect={() => {
                                                                    table
                                                                        .getColumn(
                                                                            "product_type"
                                                                        )
                                                                        ?.setFilterValue(
                                                                            type
                                                                        );
                                                                }}
                                                                className="cursor-pointer"
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        table
                                                                            .getColumn(
                                                                                "product_type"
                                                                            )
                                                                            ?.getFilterValue() ===
                                                                            type
                                                                            ? "opacity-100"
                                                                            : "opacity-0"
                                                                    )}
                                                                />
                                                                {type}
                                                            </CommandItem>
                                                        )
                                                    )}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="tags">Tags</Label>
                            <div className="col-span-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between"
                                        >
                                            {((
                                                table
                                                    .getColumn("tags")
                                                    ?.getFilterValue() as string[]
                                            )?.length || 0) + " selected" ||
                                                "Select tags..."}
                                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[200px] p-0">
                                        <Command>
                                            <CommandInput placeholder="Search tags..." />
                                            <CommandList>
                                                <CommandEmpty>
                                                    No tags found.
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    {getUniqueTags().map(
                                                        (tag: any) => {
                                                            const selectedTags =
                                                                (table
                                                                    .getColumn(
                                                                        "tags"
                                                                    )
                                                                    ?.getFilterValue() as string[]) ||
                                                                [];
                                                            const isSelected =
                                                                selectedTags.includes(
                                                                    tag
                                                                );

                                                            return (
                                                                <CommandItem
                                                                    key={tag}
                                                                    onSelect={() => {
                                                                        const column =
                                                                            table.getColumn(
                                                                                "tags"
                                                                            );
                                                                        const currentTags =
                                                                            (column?.getFilterValue() as string[]) ||
                                                                            [];

                                                                        if (
                                                                            isSelected
                                                                        ) {
                                                                            column?.setFilterValue(
                                                                                currentTags.filter(
                                                                                    (
                                                                                        t
                                                                                    ) =>
                                                                                        t !==
                                                                                        tag
                                                                                )
                                                                            );
                                                                        } else {
                                                                            column?.setFilterValue(
                                                                                [
                                                                                    ...currentTags,
                                                                                    tag,
                                                                                ]
                                                                            );
                                                                        }
                                                                    }}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            isSelected
                                                                                ? "opacity-100"
                                                                                : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {tag}
                                                                </CommandItem>
                                                            );
                                                        }
                                                    )}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="status">Status</Label>
                            <div className="col-span-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            className="w-full justify-between"
                                        >
                                            {(table
                                                .getColumn("status")
                                                ?.getFilterValue() as string) ||
                                                "All Statuses"}
                                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[200px] p-0">
                                        <Command>
                                            <CommandInput placeholder="Search statuses..." />
                                            <CommandList>
                                                <CommandEmpty>
                                                    No statuses found.
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    <CommandItem
                                                        onSelect={() => {
                                                            table
                                                                .getColumn(
                                                                    "status"
                                                                )
                                                                ?.setFilterValue(
                                                                    null
                                                                );
                                                        }}
                                                        className="cursor-pointer"
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                !table
                                                                    .getColumn(
                                                                        "status"
                                                                    )
                                                                    ?.getFilterValue()
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            )}
                                                        />
                                                        All Statuses
                                                    </CommandItem>
                                                    {[
                                                        "Not Started",
                                                        "Optimizing",
                                                        "Ready",
                                                        "Optimized",
                                                    ].map((status) => (
                                                        <CommandItem
                                                            key={status}
                                                            onSelect={() => {
                                                                table
                                                                    .getColumn(
                                                                        "status"
                                                                    )
                                                                    ?.setFilterValue(
                                                                        status
                                                                    );
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    table
                                                                        .getColumn(
                                                                            "status"
                                                                        )
                                                                        ?.getFilterValue() ===
                                                                        status
                                                                        ? "opacity-100"
                                                                        : "opacity-0"
                                                                )}
                                                            />
                                                            {status}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 items-center gap-4">
                            <Label>Price Range</Label>
                            <div className="col-span-2 space-y-2">
                                <Slider
                                    min={0}
                                    max={maxPrice}
                                    step={1}
                                    value={priceRange}
                                    onValueChange={(
                                        value: [number, number]
                                    ) => {
                                        setPriceRange(value);
                                        table
                                            .getColumn("price")
                                            ?.setFilterValue(value);
                                    }}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-sm text-muted-foreground">
                                    <span>£{priceRange[0]}</span>
                                    <span>£{priceRange[1]}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Button
                                onClick={() => {
                                    table.resetColumnFilters();
                                    table.resetSorting();
                                    table.resetPagination();
                                    setPriceRange([0, overallMaxPrice]);
                                    localStorage.removeItem(
                                        "productTable.sorting"
                                    );
                                    localStorage.removeItem(
                                        "productTable.columnFilters"
                                    );
                                    localStorage.removeItem(
                                        "productTable.columnVisibility"
                                    );
                                    localStorage.removeItem(
                                        "productTable.priceRange"
                                    );
                                }}
                            >
                                Reset
                            </Button>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
